import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import Tag from "@/components/base/Tag";
import { cases } from "@/mocks/cases";

interface FeedRow {
  caseTitle: string;
  caseSlug: string;
  org: string;
  date: string;
  content: string;
  category: string;
}

const buildFeed = (): FeedRow[] => {
  const rows: FeedRow[] = [];
  cases.forEach((item) => {
    item.officialResponses.forEach((res) => {
      rows.push({
        caseTitle: item.title,
        caseSlug: item.slug,
        org: res.org,
        date: res.date,
        content: res.content,
        category: item.category,
      });
    });
  });
  return rows.sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 4);
};

export default function OfficialResponseFeed() {
  const feed = buildFeed();

  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <SectionHeading
          eyebrow="Official Statements"
          title="官方回应汇总"
          desc="当事方与主管部门的公开说明，是核验中权重最高的材料之一。我们按时间倒序整理，并标注回应主体与对应事件。"
          action={
            <Link
              to="/fact-check"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-800"
            >
              查看核验结论
              <i className="ri-arrow-right-line text-base" aria-hidden="true" />
            </Link>
          }
        />

        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="hidden grid-cols-12 gap-4 border-b border-background-200 bg-background-50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-foreground-500 md:grid">
            <span className="col-span-3">回应主体</span>
            <span className="col-span-5">回应要点</span>
            <span className="col-span-2">对应事件</span>
            <span className="col-span-2 text-right">发布日期</span>
          </div>

          <ul className="divide-y divide-background-200">
            {feed.map((row, index) => (
              <li key={`${row.org}-${index}`} className="px-4 py-5 md:px-6">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-start md:gap-4">
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800" aria-hidden="true">
                        <i className="ri-megaphone-line text-base" />
                      </span>
                      <span className="font-heading text-sm font-bold text-foreground-950">{row.org}</span>
                    </div>
                  </div>

                  <div className="md:col-span-5">
                    <p className="text-sm leading-relaxed text-foreground-700">{row.content}</p>
                  </div>

                  <div className="md:col-span-2">
                    <Link
                      to={`/cases/${row.caseSlug}`}
                      className="line-clamp-2 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                    >
                      {row.caseTitle}
                    </Link>
                    <div className="mt-1.5">
                      <Tag tone="secondary">{row.category}</Tag>
                    </div>
                  </div>

                  <div className="text-left text-xs text-foreground-500 md:col-span-2 md:text-right">
                    <span className="flex items-center gap-1.5 md:justify-end">
                      <i className="ri-calendar-line text-sm" aria-hidden="true" />
                      {row.date}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}